import { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL, createAuthHeader } from '../config/api.config';

/**
 * Custom hook for managing experiment table data with server-side filtering
 * 
 * @param {string} clientId - The client ID for the API request
 * @param {Object|null} filters - Current active filters (filteredInfo object)
 * @param {Object} options - Additional options (timeFilter, pageSize, etc.)
 * @returns {Object} - { data, loading, error, pagination, refetch, setPage, totals }
 */
const useExperimentTableData = (clientId, filters = {}, options = {}) => {
  const {
    pageSize = 15,
    initialPage = 1,
    filterData = null,
    sorter = null,
    selectedBaseline = '4weeks',
    groupBy = null
  } = options;

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totals, setTotals] = useState(null);
  const [allExperimentIds, setAllExperimentIds] = useState([]);
  const [totalExperimentsCount, setTotalExperimentsCount] = useState(0);
  const [pagination, setPagination] = useState({
    currentPage: initialPage,
    totalPages: 1,
    totalCount: 0,
    pageSize: pageSize
  });

  // Use ref to track the latest filters to avoid stale closures
  const filtersRef = useRef(filters || {});
  filtersRef.current = filters || {};

  // Debounce timer ref
  const debounceTimer = useRef(null);
  const hasFetchedOnce = useRef(false);
  const filterDataRef = useRef(filterData);
  const currentPageRef = useRef(initialPage);

  useEffect(() => {
    filterDataRef.current = filterData;
  }, [filterData]);

  /**
   * Fetch table data from the API
   */
  const fetchTableData = useCallback(async (page = pagination.currentPage, immediateFilters = null) => {
    if (!clientId) {
      setData([]);
      return;
    }

    const resolvedPage = page ?? pagination.currentPage;
    currentPageRef.current = resolvedPage;

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error('No access token available');
      }

      // Build query parameters
      const params = new URLSearchParams();
      params.append('clientId', clientId);
      params.append('page', resolvedPage.toString());
      params.append('pageSize', pagination.pageSize.toString());

      // Add baseline parameter for comparison period
      if (selectedBaseline) {
        params.append('baseline', selectedBaseline);
      }

      // Add groupBy parameter if provided
      if (groupBy) {
        params.append('groupBy', groupBy);
      }

      // Add filter parameters in the format the API expects: filters[key]=value
      // Use the provided filters or fall back to current filters
      const appliedFilters = immediateFilters ?? filtersRef.current;
      const currentFilters = appliedFilters || {};
      filtersRef.current = currentFilters;
      
      // Map frontend filter keys to backend expected keys (singular -> plural)
      const filterKeyMapping = {
        'group': 'groups',
        'location': 'locations', 
        'status': 'statuses',
        'type': 'types',
        'storeId': 'machines',
        'products': 'products', // already plural
        'tags': 'tags', // already plural
        'route': 'routes'
      };
      
      // Add sort parameters if present
      if (sorter && sorter.columnKey && sorter.order) {
        // Map frontend sort keys to backend column names based on view mode
        let mappedColumnKey = sorter.columnKey;

        // Map unit-level column keys to grouped-level keys when in grouped mode
        // This allows the parent to maintain unit-level sort keys and have them work in grouped mode
        const unitToGroupedMapping = {
          'dynamicDelta': 'delta_units',
          'dynamicDeltaEuros': 'delta_revenue',
          'dynamicDeltaGrossProfit': 'delta_grossProfit'
        };

        const groupedToUnitMapping = {
          'delta_units': 'dynamicDelta',
          'delta_revenue': 'dynamicDeltaEuros',
          'delta_grossProfit': 'dynamicDeltaGrossProfit'
        };

        // Map sort keys based on current view mode
        if (groupBy) {
          // In grouped mode, map unit keys to grouped keys
          if (unitToGroupedMapping[sorter.columnKey]) {
            mappedColumnKey = unitToGroupedMapping[sorter.columnKey];
          }
        } else {
          // In unit mode, map grouped keys back to unit keys
          if (groupedToUnitMapping[sorter.columnKey]) {
            mappedColumnKey = groupedToUnitMapping[sorter.columnKey];
          }
        }

        params.append('sort[column]', mappedColumnKey);
        params.append('sort[order]', sorter.order === 'ascend' ? 'asc' : 'desc');

        // For delta columns, also pass the selected baseline
        // Check both unit-level and grouped delta column names
        const deltaColumns = [
          'dynamicDelta', 'dynamicDeltaEuros', 'dynamicDeltaGrossProfit', // Unit-level delta columns
          'delta_units', 'delta_revenue', 'delta_grossProfit' // Grouped delta columns
        ];
        if (deltaColumns.includes(sorter.columnKey) || deltaColumns.includes(mappedColumnKey)) {
          params.append('sort[baseline]', selectedBaseline);
        }
      }
      
      Object.entries(currentFilters).forEach(([key, values]) => {
        // Special handling for date range filter
        if (key === 'dynamicDateRange' && values && Array.isArray(values) && values.length === 2) {
          // Convert from DD/MM/YYYY to YYYY-MM-DD format
          const convertDate = (dateStr) => {
            const [day, month, year] = dateStr.split('/');
            return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
          };
          
          params.append(`filters[dateFrom]`, convertDate(values[0]));
          params.append(`filters[dateTo]`, convertDate(values[1]));
        } else if (key === 'dateFilterMode' && values) {
          params.append(`filters[dateFilterMode]`, values);
        } else if (values && Array.isArray(values) && values.length > 0) {
          // Map to backend expected key name
          const backendKey = filterKeyMapping[key] || key;
          
          // Special handling for products: convert names to IDs
          const productOptions = filterDataRef.current?.products;
          if (key === 'products' && productOptions) {
            values.forEach(productName => {
              const product = productOptions.find(p => p.name === productName);
              const productId = product ? product.id : productName;
              
              params.append(`filters[${backendKey}]`, productId);
            });
          } else {
            // For non-product filters, use values directly
            values.forEach(value => {
              params.append(`filters[${backendKey}]`, value);
            });
          }
        }
      });

      const fullUrl = `${API_BASE_URL}/experiments/overview/table?${params.toString()}`;

      const response = await axios.get(fullUrl, {
        headers: createAuthHeader(token)
      });

      const result = response.data;

      setData(result.data || []);
      setTotals(result.totals || null);

      // Extract all experiment IDs from the response
      // The backend provides allExperimentIds which contains ALL IDs across all pages
      setAllExperimentIds(result.allExperimentIds || []);

      // Set total experiments count (for grouped views, this is the actual experiment count)
      // For unit view, this will be undefined, so we fall back to pagination.totalCount
      setTotalExperimentsCount(result.totalExperimentsCount || result.pagination.totalCount);

      setPagination(prev => ({
        ...prev,
        currentPage: resolvedPage,
        totalPages: result.pagination.totalPages,
        totalCount: result.pagination.totalCount
      }));
    } catch (err) {
      console.error('Error fetching experiment table data:', err);
      setError(err);
      // Set empty data on error to allow graceful degradation
      setData([]);
      setTotals(null);
      setAllExperimentIds([]);
      setPagination(prev => ({
        ...prev,
        totalPages: 1,
        totalCount: 0
      }));
    } finally {
      hasFetchedOnce.current = true;
      setLoading(false);
    }
  }, [clientId, pagination.pageSize, sorter, selectedBaseline, groupBy]);

  /**
   * Debounced fetch function for filter changes
   */
  const debouncedFetch = useCallback((page = 1, immediateFilters = null) => {
    // Clear existing timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set new timer
    debounceTimer.current = setTimeout(() => {
      fetchTableData(page, immediateFilters ?? {});
    }, 300); // 300ms debounce
  }, [fetchTableData]);

  /**
   * Set current page and fetch data
   */
  const setPage = useCallback((page) => {
    currentPageRef.current = page;
    setPagination(prev => ({ ...prev, currentPage: page }));
    fetchTableData(page);
  }, [fetchTableData]);

  /**
   * Refetch data with current filters and pagination
   */
  const refetch = useCallback((immediateFilters = null, options = {}) => {
    const { skipDebounce = false } = options;
    const hasProvidedFilters = immediateFilters !== null;
    const normalizedFilters = (immediateFilters ?? filtersRef.current) || {};
    
    if (hasProvidedFilters) {
      currentPageRef.current = 1;
      setPagination(prev => ({ ...prev, currentPage: 1 }));
    }

    const targetPage = hasProvidedFilters ? 1 : currentPageRef.current;

    if (skipDebounce || !hasFetchedOnce.current) {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
        debounceTimer.current = null;
      }
      fetchTableData(targetPage, normalizedFilters);
    } else {
      debouncedFetch(targetPage, normalizedFilters);
    }
  }, [debouncedFetch, fetchTableData]);

  // Combined effect to handle both initial load and filter/sort changes
  useEffect(() => {
    if (!clientId) return;

    const filtersReady = filters !== null;
    const hasSorter = Boolean(sorter?.columnKey && sorter?.order);

    if (!filtersReady && !hasSorter) {
      return;
    }

    const normalizedFilters = filtersReady ? filters : filtersRef.current;
    const shouldSkipDebounce = !hasFetchedOnce.current;

    refetch(normalizedFilters, { skipDebounce: shouldSkipDebounce });
  }, [clientId, filters, sorter, refetch]); // eslint-disable-line react-hooks/exhaustive-deps


  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  return {
    data,
    loading,
    error,
    pagination,
    refetch,
    setPage,
    totals,
    allExperimentIds,
    totalExperimentsCount,
    viewMode: groupBy || 'unit'
  };
};

export default useExperimentTableData;
